import Fuse from 'fuse.js';
import { ArrowDownAZ, ArrowUpZA } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Github, Linkedin } from '@/components/icons';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { githubHandle } from '@/lib/utils';
import type { EventAttendee } from '@/types';

const DiscordIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>Discord</title>
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
);

type SortMode = 'name' | 'team' | 'track';
type SortDir = 'asc' | 'desc';

const SORT_MODES: SortMode[] = ['name', 'team', 'track'];
const SORT_LABELS: Record<SortMode, string> = {
  name: 'Nombre',
  team: 'Equipo',
  track: 'Track',
};

const NONE_KEY = '__none__';

function naturalCompare(a: string, b: string) {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

function nameCompare(a: string, b: string) {
  return a.localeCompare(b, undefined, { sensitivity: 'base' });
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

interface Group {
  key: string;
  label: string | null;
  rows: EventAttendee[];
}

function groupAttendees(
  attendees: EventAttendee[],
  sortMode: SortMode,
  dirMul: number,
): Group[] {
  if (sortMode === 'name') {
    const rows = [...attendees].sort(
      (a, b) => dirMul * nameCompare(a.fullName, b.fullName),
    );
    return [{ key: 'all', label: null, rows }];
  }

  const field = sortMode === 'team' ? 'teamSlug' : 'trackName';
  const compare = sortMode === 'team' ? naturalCompare : nameCompare;

  const byKey = new Map<string, EventAttendee[]>();
  for (const a of attendees) {
    const key = a[field] ?? NONE_KEY;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key)?.push(a);
  }

  const keys = [...byKey.keys()].sort((a, b) => {
    if (a === NONE_KEY) return 1;
    if (b === NONE_KEY) return -1;
    return dirMul * compare(a, b);
  });

  const noneLabel = sortMode === 'team' ? 'Sin equipo' : 'Sin track';

  return keys.map((key) => ({
    key,
    label: key === NONE_KEY ? noneLabel : key,
    rows: (byKey.get(key) ?? []).sort((a, b) =>
      nameCompare(a.fullName, b.fullName),
    ),
  }));
}

function readParam<T extends string>(
  params: URLSearchParams,
  key: string,
  allowed: readonly T[],
  fallback: T,
): T {
  const value = params.get(key);
  return (allowed as readonly string[]).includes(value ?? '')
    ? (value as T)
    : fallback;
}

export function AttendeesList({ attendees }: { attendees: EventAttendee[] }) {
  const [query, setQuery] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [hydrated, setHydrated] = useState(false);

  // Read initial state from the URL after mount, so the very first sync
  // effect below doesn't stomp a shared link's query before we've read it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setQuery(params.get('q') ?? '');
    setSortMode(readParam(params, 'sort', SORT_MODES, 'name'));
    setSortDir(readParam(params, 'dir', ['asc', 'desc'] as const, 'asc'));
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    const handle = setTimeout(() => {
      const params = new URLSearchParams(window.location.search);
      params.delete('q');
      params.delete('sort');
      params.delete('dir');
      if (query.trim()) params.set('q', query.trim());
      if (sortMode !== 'name') params.set('sort', sortMode);
      if (sortDir !== 'asc') params.set('dir', sortDir);
      const qs = params.toString();
      window.history.replaceState(
        null,
        '',
        qs ? `?${qs}` : window.location.pathname,
      );
    }, 250);
    return () => clearTimeout(handle);
  }, [query, sortMode, sortDir, hydrated]);

  const fuse = useMemo(
    () =>
      new Fuse(attendees, {
        keys: ['fullName', 'teamSlug', 'mentorName', 'projectName'],
        threshold: 0.35,
        ignoreLocation: true,
      }),
    [attendees],
  );

  const filtered = useMemo(() => {
    const q = query.trim();
    if (!q) return attendees;
    return fuse.search(q).map((result) => result.item);
  }, [query, fuse, attendees]);

  const dirMul = sortDir === 'asc' ? 1 : -1;
  const groups = useMemo(
    () => groupAttendees(filtered, sortMode, dirMul),
    [filtered, sortMode, dirMul],
  );

  const showTeamColumn = sortMode !== 'team';
  const showTrackColumn = sortMode !== 'track';
  const total = filtered.length;

  return (
    <div className="space-y-6">
      <p className="text-muted-foreground text-sm">
        {total} de {attendees.length} asistentes
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Buscar por nombre, equipo, mentor o proyecto..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="min-w-[240px] flex-1"
        />
        <Select
          value={sortMode}
          onValueChange={(v) => setSortMode(v as SortMode)}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_MODES.map((mode) => (
              <SelectItem key={mode} value={mode}>
                {SORT_LABELS[mode]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="icon"
          onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
          aria-label={
            sortDir === 'asc' ? 'Orden ascendente' : 'Orden descendente'
          }
        >
          {sortDir === 'asc' ? (
            <ArrowDownAZ className="h-4 w-4" />
          ) : (
            <ArrowUpZA className="h-4 w-4" />
          )}
        </Button>
      </div>

      {total === 0 ? (
        <p className="py-12 text-center text-muted-foreground">
          No se encontraron asistentes.
        </p>
      ) : (
        <div className="space-y-8">
          {groups.map((group) => (
            <div key={group.key} className="space-y-2">
              {group.label && (
                <h3 className="font-semibold text-base">
                  {group.label}{' '}
                  <span className="text-muted-foreground text-sm">
                    ({group.rows.length})
                  </span>
                </h3>
              )}
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-primary hover:bg-primary">
                      <TableHead />
                      <TableHead className="text-primary-foreground">
                        Nombre
                      </TableHead>
                      {showTeamColumn && (
                        <TableHead className="text-primary-foreground">
                          Equipo
                        </TableHead>
                      )}
                      <TableHead className="text-primary-foreground">
                        Mentor
                      </TableHead>
                      <TableHead className="text-primary-foreground">
                        Proyecto
                      </TableHead>
                      {showTrackColumn && (
                        <TableHead className="text-primary-foreground">
                          Track
                        </TableHead>
                      )}
                      <TableHead className="text-primary-foreground">
                        Redes
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {group.rows.map((a) => {
                      const handle = githubHandle(a.github);
                      return (
                        <TableRow key={a.hackerId}>
                          <TableCell className="w-10">
                            <Avatar className="h-8 w-8">
                              {handle && (
                                <AvatarImage
                                  src={`https://github.com/${handle}.png`}
                                  alt={`${a.fullName} avatar`}
                                />
                              )}
                              <AvatarFallback className="text-xs">
                                {getInitials(a.fullName)}
                              </AvatarFallback>
                            </Avatar>
                          </TableCell>
                          <TableCell className="font-medium">
                            {a.fullName}
                          </TableCell>
                          {showTeamColumn && (
                            <TableCell>{a.teamSlug ?? '—'}</TableCell>
                          )}
                          <TableCell>{a.mentorName ?? '—'}</TableCell>
                          <TableCell>
                            {a.projectName ? (
                              <div className="flex items-center gap-2">
                                {a.projectLogoUrl ? (
                                  <img
                                    src={a.projectLogoUrl}
                                    alt=""
                                    className="size-8 shrink-0 border border-border object-cover"
                                  />
                                ) : (
                                  <div className="size-8 shrink-0 border border-border bg-muted" />
                                )}
                                {a.projectRepoUrl ? (
                                  <a
                                    href={a.projectRepoUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary hover:underline"
                                  >
                                    {a.projectName}
                                  </a>
                                ) : (
                                  a.projectName
                                )}
                              </div>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          {showTrackColumn && (
                            <TableCell>{a.trackName ?? '—'}</TableCell>
                          )}
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {handle && (
                                <a
                                  href={`https://github.com/${handle}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-muted-foreground hover:text-foreground"
                                  aria-label="GitHub"
                                >
                                  <Github className="h-4 w-4" />
                                </a>
                              )}
                              {a.linkedin && (
                                <a
                                  href={a.linkedin}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-muted-foreground hover:text-foreground"
                                  aria-label="LinkedIn"
                                >
                                  <Linkedin className="h-4 w-4" />
                                </a>
                              )}
                              {a.discordId && (
                                <a
                                  href={`https://discord.com/users/${a.discordId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-muted-foreground hover:text-foreground"
                                  aria-label="Discord"
                                >
                                  <DiscordIcon className="h-4 w-4" />
                                </a>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
